import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import type { TemplateDetail, TemplateSummary } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { formatMinutes } from '../lib/dates';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, PageHeader } from '../components/ui';
import { PlanBuilder } from '../components/plan/PlanBuilder';
import { DetailsForm } from '../components/sessions/DetailsForm';

export function TemplatesPage() {
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ['templates'], queryFn: () => api.get<TemplateSummary[]>('/templates') });
  return (
    <>
      <PageHeader
        title="Templates"
        description="Session plans you reuse. Start a new session from one, then adjust it for the day. Templates are yours alone."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden /> New template
          </Button>
        }
      />
      {list.isLoading ? (
        <LoadingBlock />
      ) : list.isError ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : !list.data?.length ? (
        <Empty title="No templates yet" action={<Button variant="secondary" onClick={() => setCreating(true)}>Make a template</Button>}>
          Build one here, or open a session you like and choose Save as template.
        </Empty>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
          {list.data.map((t) => (
            <li key={t.id}>
              <Link to={`/templates/${t.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-sunken sm:px-5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{t.title}</p>
                  <p className="tnum mt-0.5 text-[13px] text-muted">
                    {formatMinutes(t.durationMinutes)} session, {t.drillCount} {t.drillCount === 1 ? 'drill' : 'drills'}
                    {t.focus ? `, ${t.focus}` : ''}
                  </p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-faint" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <NewTemplateDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function NewTemplateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState('90');
  const create = useMutation({
    mutationFn: () => api.post<TemplateDetail>('/templates', { title, durationMinutes: duration }),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      onClose();
      navigate(`/templates/${t.id}`);
    },
  });
  const f = fieldErrors(create.error);
  return (
    <Dialog open={open} onClose={onClose} title="New template">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
        {create.isError && !Object.keys(f).length && <Alert>{errorMessage(create.error)}</Alert>}
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <Field label="Name" value={title} onChange={(e) => setTitle(e.target.value)} error={f.title} placeholder="e.g. Standard Tuesday" autoFocus />
          <Field label="Length (min)" type="number" min={10} max={360} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} error={f.durationMinutes} className="field-input tnum" />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create and plan
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function TemplatePage() {
  const { templateId = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ['template', templateId];
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const q = useQuery({ queryKey: key, queryFn: () => api.get<TemplateDetail>(`/templates/${templateId}`) });
  const remove = useMutation({
    mutationFn: () => api.del(`/templates/${templateId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      navigate('/templates', { replace: true });
    },
  });

  if (q.isLoading) return <LoadingBlock />;
  if (q.isError || !q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const t = q.data;
  const patch = async (body: Record<string, unknown>) => {
    const next = await api.patch<TemplateDetail>(`/templates/${templateId}`, body);
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: ['templates'] });
    return next;
  };

  return (
    <>
      <Link to="/templates" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Templates
      </Link>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-[24px] leading-tight sm:text-[28px]">{t.title}</h1>
          <p className="tnum mt-1.5 text-muted">
            Template, {formatMinutes(t.durationMinutes)}
            {t.focus ? `. Focus: ${t.focus}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setEditing(!editing)}>
            <Pencil className="size-4" aria-hidden /> Edit details
          </Button>
          <Button variant="danger" onClick={() => setDeleting(true)}>
            <Trash2 className="size-4" aria-hidden /> Delete
          </Button>
        </div>
      </header>
      {editing && (
        <section className="mb-8 rounded-lg border border-line bg-surface p-4 sm:p-5">
          <DetailsForm value={t} onSave={patch} onDone={() => setEditing(false)} isTemplate />
        </section>
      )}
      <PlanBuilder
        key={t.id}
        initialPlan={t.plan}
        version={t.version}
        meta={{ startTime: null, durationMinutes: t.durationMinutes, courtsAvailable: t.courtsAvailable, playersExpected: t.playersExpected }}
        save={async (plan, version) => {
          const next = await api.put<TemplateDetail>(`/templates/${templateId}/plan`, { version, plan });
          qc.setQueryData(key, next);
          void qc.invalidateQueries({ queryKey: ['templates'] });
          return next;
        }}
      />
      <Dialog open={deleting} onClose={() => setDeleting(false)} title={`Delete ${t.title}?`}>
        <p className="text-muted">Sessions you already made from it aren’t affected.</p>
        {remove.isError && <div className="mt-3"><Alert>{errorMessage(remove.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleting(false)}>
            Keep it
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete template
          </Button>
        </div>
      </Dialog>
    </>
  );
}
